import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core'
import { useCheckSsoAccess } from '../../../api/users/sso'
import SydButton from '../../commonDesign/Button'
import { useOutboundSso } from './OutboundSsoProvider'

const useStyles = makeStyles((theme) => ({
  ssoLink: {
    transition: 'filter .3s ease-in-out',
    filter: 'opacity(100%)',
    '&:hover': {
      cursor: 'pointer'
    },
    '&.__disabled': {
      filter: 'opacity(30%)',
      pointerEvents: 'none',
      '&:hover': {
        cursor: 'default'
      }
    }
  }
}))

const OutboundSsoButton = React.forwardRef(function OutboundSsoLink ({
  children,
  provider,
  sameTab,
  className,
  relay = undefined,
  disabledText = null,
  loadingText = 'Loading...',
  ...buttonProps
}, ref) {
  const { data, isLoading } = useCheckSsoAccess(provider)
  const osp = useOutboundSso()

  const disabled = useMemo(() => (!data?.hasAccess || !data.ssoProvider), [data])
  const handleClick = useCallback(async () => {
    if (disabled) return
    return await osp.start(provider, { sameTab, ssoProvider: data.ssoProvider }, relay)
  }, [disabled, osp, provider, sameTab, data, relay])
  const classes = useStyles({ disabled })

  if (isLoading) {
    return (
      <SydButton className={'flex gap-2 items-center ' + className} {...buttonProps} disabled processing>
        {loadingText}
      </SydButton>
    )
  }

  if (disabled) {
    return (
      <SydButton className={clsx(classes.ssoLink, className)} {...buttonProps} disabled>
        {disabledText ?? children}
      </SydButton>
    )
  }

  return (
    <SydButton
      ref={ref}
      className={clsx(classes.ssoLink, className)}
      {...buttonProps}
      onClick={handleClick}
    >
      {children}
    </SydButton>
  )
})

OutboundSsoButton.propTypes = {
  children: PropTypes.node,
  provider: PropTypes.string,
  sameTab: PropTypes.bool,
  className: PropTypes.string,
  relay: PropTypes.object,
  disabledText: PropTypes.node,
  loadingText: PropTypes.string
}

OutboundSsoButton.defaultProps = {
  sameTab: false
}

export default OutboundSsoButton
